import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { AgentEvents } from '@dreamai/types';
import api from '../../../api';
import { usePusher } from '../../../contexts/PusherContext';
import { useProjectAppLoading } from '../../../providers/ProjectAppViewLoadingProvider';
import ArtifactOpener from './ArtifactOpener';
import ProjectAppEmptyState from './ProjectAppEmptyState';
import './project-app-generated-page.scss';

interface ProjectAppGeneratedPageProps {
  appId: string;
  slug: string;
}

interface ViewResponse {
  success: boolean;
  appId: string;
  slug: string;
  name: string;
  version: number;
  html: string;
}

interface ViewUpdatedEvent {
  threadId: string;
  appId: string;
  slug: string;
  version: number;
  timestamp: string;
}

/**
 * Renders a generated project app view inside a sandboxed iframe.
 *
 * Wires up a postMessage bridge so the generated page can:
 *   - receive an auth token (window.__DREAM_AUTH_TOKEN__ + 'dream-ready' event)
 *   - navigate the parent app via { type: 'navigate', to: '/...' }
 *
 * The iframe scrolls its own content (no auto-resize) so the surrounding
 * frame stays put.
 *
 * Refetches the HTML when the PVA pushes PROJECT_APP_VIEW_UPDATED for this slug.
 */
const ProjectAppGeneratedPage: React.FC<ProjectAppGeneratedPageProps> = ({
  appId,
  slug,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const navigate = useNavigate();
  const { finishedKey } = useProjectAppLoading();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [view, setView] = useState<ViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // True only once the iframe's onLoad has fired for the current srcDoc.
  // Until then we keep the spinner overlay visible and hide the iframe to
  // prevent flashes of unstyled HTML before the page's <style>/<link> tags
  // have been parsed and applied.
  const [iframeReady, setIframeReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the iframe posts `dream-open-artifact` — drives the modal
  // mount/unmount lifecycle so closing returns control to the iframe.
  const [openArtifactId, setOpenArtifactId] = useState<string | null>(null);

  const apiBase = process.env.REACT_APP_API_URL || '';

  // ── Build the runtime bridge script injected into the iframe ──
  const wrappedHtml = useMemo(() => {
    if (!view?.html) return '';
    const bridge = `
<script>
(function() {
  window.__DREAM_API_BASE__ = ${JSON.stringify(apiBase)};
  window.__DREAM_APP_ID__ = ${JSON.stringify(appId)};
  window.__DREAM_VIEW_SLUG__ = ${JSON.stringify(slug)};
  window.__DREAM_AUTH_TOKEN__ = null;

  // Listen for messages from parent
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'dream-auth' && typeof e.data.token === 'string') {
      window.__DREAM_AUTH_TOKEN__ = e.data.token;
      window.dispatchEvent(new Event('dream-ready'));
    }
    if (e.data && e.data.type === 'dream-state-refetch' && window.__DREAM_AUTH_TOKEN__) {
      fetch(window.__DREAM_API_BASE__ + '/project-apps/' + window.__DREAM_APP_ID__ + '/state', {
        headers: { Authorization: 'Bearer ' + window.__DREAM_AUTH_TOKEN__ }
      })
        .then(function(r) { return r.json(); })
        .then(function(json) {
          if (json && json.data) {
            window.dispatchEvent(new CustomEvent('dream-state-updated', { detail: json.data }));
          }
        })
        .catch(function() {});
    }
  });

  // Signal we're ready to receive the auth token
  function ready() {
    try { parent.postMessage({ type: 'dream-iframe-ready' }, '*'); } catch (_) {}
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    ready();
  } else {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  }

})();
</script>`;

    // Inject the bridge as the first thing in <head>, falling back to prepending if no <head>
    if (/<head[^>]*>/i.test(view.html)) {
      return view.html.replace(/<head([^>]*)>/i, `<head$1>${bridge}`);
    }
    return bridge + view.html;
  }, [view?.html, apiBase, appId, slug]);

  // ── Fetch the view HTML ──
  const fetchView = useCallback(async () => {
    try {
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await api.get<ViewResponse>(
        `/project-apps/${appId}/view/${slug}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) setView(res.data);
      else setError('Failed to load view');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setError('not-found');
      } else {
        console.error('Error fetching project app view:', err);
        setError(err?.message || 'Failed to load view');
      }
    } finally {
      setLoading(false);
    }
  }, [appId, slug, getAccessTokenSilently]);

  useEffect(() => {
    setLoading(true);
    setIframeReady(false);
    fetchView();
  }, [fetchView, finishedKey]);

  // Reset iframe-ready whenever the HTML we're about to render changes,
  // so the loader covers re-fetches (e.g. after a PVA view update).
  useEffect(() => {
    setIframeReady(false);
  }, [wrappedHtml]);

  // ── Listen for Pusher view update events ──
  useEffect(() => {
    const channel = pusherContext?.pusherChannel;
    if (!channel) return;

    const handler = (data: ViewUpdatedEvent) => {
      if (data.appId !== appId || data.slug !== slug) return;
      fetchView();
    };

    channel.bind(AgentEvents.PROJECT_APP_VIEW_UPDATED, handler);
    return () => {
      channel.unbind(AgentEvents.PROJECT_APP_VIEW_UPDATED, handler);
    };
  }, [pusherContext?.pusherChannel, appId, slug, fetchView]);

  // ── Listen for Pusher state-updated events (realtime state sync) ──
  useEffect(() => {
    const pusherInstance = (pusherContext as any)?.pusherInstance;
    const pusherEnv = process.env.REACT_APP_PUSHER_ENV;
    if (!pusherInstance || !pusherEnv) return;

    const channelName = `${pusherEnv}-project-app-state-${appId}`;
    const channel = pusherInstance.subscribe(channelName);

    channel.bind('state-updated', () => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'dream-state-refetch' },
          '*',
        );
      }
    });

    return () => {
      channel.unbind_all();
      pusherInstance.unsubscribe(channelName);
    };
  }, [(pusherContext as any)?.pusherInstance, appId]);

  // ── PostMessage bridge: handle messages from iframe ──
  useEffect(() => {
    const sendAuth = async () => {
      try {
        const token = await getAccessTokenSilently();
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'dream-auth', token },
          '*',
        );
      } catch (err) {
        console.error('Error sending auth token to iframe:', err);
      }
    };

    const handleMessage = (e: MessageEvent) => {
      // Only accept messages from our iframe
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow)
        return;
      const data = e.data;
      if (!data || typeof data !== 'object') return;

      switch (data.type) {
        case 'dream-iframe-ready':
          sendAuth();
          break;
        case 'dream-navigate':
          if (typeof data.to === 'string' && data.to.startsWith('/')) {
            navigate(data.to);
          }
          break;
        case 'dream-open-artifact':
          if (typeof data.artifactId === 'string' && data.artifactId) {
            setOpenArtifactId(data.artifactId);
          }
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [getAccessTokenSilently, navigate]);

  // View doesn't exist yet (Dream is mid-generation) — show the
  // "Getting your project ready" empty state.
  if (error === 'not-found') {
    return (
      <div className='project-app-generated-page'>
        <ProjectAppEmptyState building />
      </div>
    );
  }

  if (error) {
    return (
      <div className='project-app-generated-page__error'>
        <p>Couldn't load the project page.</p>
        <small>{error}</small>
      </div>
    );
  }

  // View exists but has no HTML yet — render the empty state instead of a
  // blank transparent iframe.
  if (!loading && view && !view.html?.trim()) {
    return (
      <div className='project-app-generated-page'>
        <ProjectAppEmptyState building />
      </div>
    );
  }

  const showLoader = loading || !view || !iframeReady;

  return (
    <div className='project-app-generated-page'>
      {view && (
        <iframe
          ref={iframeRef}
          title={view.name || 'Project page'}
          srcDoc={wrappedHtml}
          sandbox='allow-scripts allow-same-origin allow-forms allow-popups'
          onLoad={() => setIframeReady(true)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            // Hide the iframe until its document has loaded so we never
            // flash unstyled markup at the user.
            visibility: iframeReady ? 'visible' : 'hidden',
          }}
        />
      )}
      {showLoader && (
        <div className='project-app-generated-page__loading-overlay'>
          <div className='loading-spinner' />
        </div>
      )}
      {openArtifactId && (
        <ArtifactOpener
          key={openArtifactId}
          artifactId={openArtifactId}
          hideButton
          defaultOpen
          onClose={() => setOpenArtifactId(null)}
        />
      )}
    </div>
  );
};

export default ProjectAppGeneratedPage;
